/**
 * Personagens (dono, 2026-10-17): os dados do personagem em CARDS (arte de corpo inteiro da classe, public/class/N.png).
 * Mesmas APIs/acoes do painel antigo: /Character/get, /Character/pk (limpa a PK de TODOS os personagens offline da
 * conta -- por isso fica no topo, nao em cada card) e /Character/delete (confirma digitando o nome).
 */
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Loader2, Skull, Trash2 } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { deleteCharacter, getCharacters, removePk, type Character } from '@/shared/services/characterService';
import { CLASSES_PT, NATION_COLORS } from '@/features/Admin/labels';
import { NATION_NAME } from '@/shared/lib/war';
import { NxModal, errMsg } from './NxModal';

type Notice = { tone: 'ok' | 'error'; text: string } | null;

export default function NextCharactersPage() {
  const { refreshCash } = useAuth();
  const [chars, setChars] = useState<Character[] | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [pkOpen, setPkOpen] = useState(false);
  const [target, setTarget] = useState<Character | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    getCharacters()
      .then((list) => setChars([...list].sort((a, b) => b.lev - a.lev)))
      .catch(() => setChars([]));

  useEffect(() => {
    void load();
  }, []);

  const clearPk = async () => {
    setBusy(true);
    try {
      const res = await removePk();
      setNotice({ tone: 'ok', text: res?.message || 'Penalidade PK removida.' });
    } catch (e: any) {
      setNotice({ tone: 'error', text: errMsg(e, 'Não foi possível limpar a PK.') });
    } finally {
      setBusy(false);
      setPkOpen(false);
    }
  };

  const remove = async () => {
    if (!target || typed !== target.name) return;
    setBusy(true);
    try {
      const res = await deleteCharacter(target.characterIdx);
      setNotice({ tone: 'ok', text: res?.message || 'Personagem deletado.' });
      setTarget(null);
      void load();
      void refreshCash();
    } catch (e: any) {
      setNotice({ tone: 'error', text: errMsg(e, 'Não foi possível deletar o personagem.') });
      setTarget(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nxd-stack">
      <header className="nxd-heading nxd-heading--row">
        <div>
          <p className="nx-eyebrow">Painel do jogador</p>
          <h1>Personagens</h1>
        </div>
        {!!chars?.length && (
          <button type="button" className="nxd-btn" onClick={() => setPkOpen(true)}>
            <Skull size={15} /> Limpar PK
          </button>
        )}
      </header>

      {notice && <p className={`nxd-notice nxd-notice--${notice.tone}`}>{notice.text}</p>}

      {chars === null ? (
        <div className="nxd-loading">
          <Loader2 size={26} className="nxb-spin" />
        </div>
      ) : chars.length === 0 ? (
        <div className="nxd-card nxd-empty">Nenhum personagem na conta ainda. Crie o seu no jogo!</div>
      ) : (
        <div className="nxd-chars">
          {chars.map((c) => {
            const classId = c['class'] ?? c.style;
            const nationColor = NATION_COLORS[c.nation] || '#8fa3bd';
            return (
              <article key={c.characterIdx} className="nxd-char" style={{ '--nation': nationColor } as CSSProperties}>
                <div className="nxd-char__art">
                  <img src={`/class/${classId}.png`} alt="" loading="lazy" />
                  <span className="nxd-char__lv">Lv. {c.lev}</span>
                </div>
                <div className="nxd-char__body">
                  <h3 title={c.name}>{c.name}</h3>
                  <p className="nxd-char__class">{CLASSES_PT[classId] || `Classe ${classId}`}</p>
                  <span className="nxd-char__nation">{NATION_NAME[c.nation] || 'Sem nação'}</span>
                  <dl className="nxd-char__stats">
                    <div>
                      <dt>STR</dt>
                      <dd>{c.str ?? 0}</dd>
                    </div>
                    <div>
                      <dt>DEX</dt>
                      <dd>{c.dex ?? 0}</dd>
                    </div>
                    <div>
                      <dt>INT</dt>
                      <dd>{c.int ?? 0}</dd>
                    </div>
                  </dl>
                  <div className="nxd-char__alz">
                    <span>Alz</span>
                    <b>{(c.alz ?? 0).toLocaleString('pt-BR')}</b>
                  </div>
                  <button type="button" className="nxd-char__del" onClick={() => (setTyped(''), setTarget(c))}>
                    <Trash2 size={14} /> Deletar
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <NxModal open={pkOpen} onClose={() => setPkOpen(false)} busy={busy} className="nxb-modal--login">
        <span className="nxb-badge">
          <Skull size={28} />
        </span>
        <h2 className="nxb-title nxb-title--center">
          Limpar <span>PK</span>
        </h2>
        <p className="nxb-lead nxb-lead--center">
          Remove a penalidade PK de todos os personagens da conta que estiverem <b>fora do jogo</b>. Não dá para desfazer.
        </p>
        <button type="button" className="nxb-submit" onClick={() => void clearPk()} disabled={busy}>
          {busy ? <Loader2 size={18} className="nxb-spin" /> : null}
          {busy ? 'Limpando...' : 'Limpar PK'}
        </button>
      </NxModal>

      <NxModal open={!!target} onClose={() => setTarget(null)} busy={busy} className="nxb-modal--login nxb-modal--danger">
        <span className="nxb-badge nxb-badge--danger">
          <Trash2 size={26} />
        </span>
        <h2 className="nxb-title nxb-title--center">
          Deletar <span>personagem</span>
        </h2>
        <p className="nxb-lead nxb-lead--center">
          <b>{target?.name}</b> (Lv. {target?.lev}) será apagado para sempre. Para confirmar, digite o nome dele.
        </p>
        <label className="nxb-field">
          <span>Nome do personagem</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={target?.name} autoComplete="off" />
        </label>
        <button
          type="button"
          className="nxb-submit nxb-submit--danger"
          onClick={() => void remove()}
          disabled={busy || !target || typed !== target.name}
        >
          {busy ? <Loader2 size={18} className="nxb-spin" /> : null}
          {busy ? 'Deletando...' : 'Deletar para sempre'}
        </button>
      </NxModal>
    </div>
  );
}
